import { ArilynLibrary } from './ALEvents';
import musicAsset from './assets/audio/new_file.mp3';

export class AudioPlayer {
  private readonly audioProgress = new ArilynLibrary.ALEvent<number>();
  public get AudioProgressEvent() {
    return this.audioProgress.expose();
  }

  private context?: AudioContext;
  private audioClips: AudioClip[] = [];
  private loadedCount = 0;

  constructor() {
    this.createContext();
    if (this.context) {
      let clip = new AudioClip('music', musicAsset, this.context, true);
      clip.LoadedEvent.addEventListener(this.onClipLoaded.bind(this));
      this.audioClips.push(clip);
    }
    if (this.audioClips.length == 0) this.audioProgress.invoke(1);
  }

  catchInteraction() {
    this.context?.resume();
  }

  playAmbient(clipName: string) {
    this.audioClips.forEach((clip) => clip.stop());
    this.setLoopingVolume(1);
    let clip = this.audioClips.find((x) => x.name == clipName);
    if (clip) clip.play();
  }

  play(clipName: string) {
    let clip = this.audioClips.find((x) => x.name == clipName);
    if (clip) clip.play();
  }

  stop(clipName: string) {
    let clip = this.audioClips.find((x) => x.name == clipName);
    if (clip) clip.stop();
  }

  setMuted(muted: boolean) {
    this.audioClips.forEach((clip) => clip.setMuted(muted));
  }

  setVolume(clipName: string, volume: number) {
    let clip = this.audioClips.find((x) => x.name == clipName);
    if (clip) clip.setVolume(volume);
  }

  setLoopingVolume(volume: number) {
    this.audioClips.forEach((clip) => {
      if (clip.loop) clip.setVolume(volume);
    });
  }

  private createContext() {
    try {
      //@ts-ignore
      window.AudioContext = window.AudioContext || window.webkitAudioContext;
      this.context = new AudioContext();
    } catch (e) {
      console.error('Web Audio API is not supported in this browser');
    }
  }

  private onClipLoaded() {
    this.loadedCount++;
    let progress = this.loadedCount / this.audioClips.length;
    this.audioProgress.invoke(progress);
  }
}

export class AudioClip {
  private readonly loaded = new ArilynLibrary.ALEvent<number>();
  public get LoadedEvent() {
    return this.loaded.expose();
  }

  name: string;
  loop: boolean = false;

  private buffer?: AudioBuffer;
  private context: AudioContext;
  private source?: AudioBufferSourceNode;
  private gainNode?: GainNode;
  private volume: number = 1;
  private muted: boolean = false;

  constructor(clipName: string, url: string, ctx: AudioContext, shouldLoop: boolean) {
    this.name = clipName;
    this.context = ctx;
    this.loop = shouldLoop;
    this.loadAudio(url);
  }

  play() {
    this.stop();
    if (this.buffer && this.gainNode) {
      this.source = this.context.createBufferSource();
      this.source.buffer = this.buffer;
      this.source.loop = this.loop;
      this.source.connect(this.gainNode);
      this.source.start(0);
      if (this.muted) this.gainNode.gain.cancelScheduledValues(0);
    }
  }

  stop() {
    if (this.source) {
      this.source.stop();
      this.source.disconnect();
      this.source = undefined;
    }
  }

  setVolume(volume: number) {
    if (this.gainNode) {
      if (!this.muted)
        this.gainNode.gain.exponentialRampToValueAtTime(volume, this.context.currentTime + 2);
      this.volume = volume;
    }
  }

  setMuted(muted: boolean) {
    if (!this.gainNode) return;
    if (muted) this.gainNode.gain.cancelScheduledValues(0);
    this.gainNode.gain.value = muted ? 0 : this.volume;
    this.muted = muted;
  }

  private loadAudio(url: string) {
    var request = new XMLHttpRequest();
    request.open('GET', url, true);
    request.responseType = 'arraybuffer';

    request.onload = () => {
      this.context.decodeAudioData(request.response, this.onDecode.bind(this));
    };
    request.send();
  }

  private onDecode(buffer: AudioBuffer) {
    this.buffer = buffer;
    this.gainNode = this.context.createGain();
    this.gainNode.connect(this.context.destination);
    this.loaded.invoke();
  }
}
